import {Injector} from '@angular/core';
import {L10nTranslationService} from 'angular-l10n';
import {AsyncSubject, BehaviorSubject, defer, EMPTY, merge, Observable, of, ReplaySubject, Subject, timer} from 'rxjs';
import {distinctUntilChanged, filter, finalize, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';
import {bindWith} from '../../common/rxjs/bind-with';
import {squash} from '../../common/rxjs/squash';
import {InvPortfolioService} from '../../components/investment-portfolio';
import {WIDGET_DESCRIPTION} from '../../components/layout/widget/widget-description';
import {
    WidgetImplementation,
    WidgetImplementationEvent,
    WidgetImplementationEventFlags
} from '../../components/layout/widget/widget-implementation';
import {WIDGET_UPDATES_GATING$} from '../../components/layout/widget/widget-updates-gating';
import {SecurityService} from '../../components/security';
import {QueryLangFilterOperator} from '../../enums/query-lang-filter-operator';
import {ListFilterFactory} from '../../factories/list-filter.factory';
import {ExchangeAccountId} from '../../models/exchange-account';
import {QueryLangFilterCriterion} from '../../models/filters/query-lang-filter-criterion';
import {Order} from '../../models/order';
import {isSecurityKeysEqual, SecurityKey} from '../../models/security-key';
import {OrderService} from '../../services/orders';
import {
    getSettingsWithUpdateField,
    getWidgetSettingsWithUpdatesStream,
    hasSettingUpdateField,
    setWidgetSettings,
    updateWidgetSettings,
    widgetImplDestroy$
} from '../components/common-widget.implementation';
import {OrderStreamingService} from '../components/order-streaming';
import {OrderDetailsWidgetSettings, OrderDetailsWidgetSettingsService} from './settings';

export class OrderDetailsWidgetImplementation implements WidgetImplementation {
    public readonly widgetDescription = this.injector.get(WIDGET_DESCRIPTION);

    public settings: OrderDetailsWidgetSettings;

    public readonly settingsService: OrderDetailsWidgetSettingsService = this.injector
        .get(OrderDetailsWidgetSettingsService);

    public ready$ = new BehaviorSubject<boolean>(false);
    public event$ = new Subject<WidgetImplementationEvent>();

    public readonly settingsWithUpdates$: Observable<Partial<OrderDetailsWidgetSettings>> =
                        getWidgetSettingsWithUpdatesStream(this);

    public readonly title$: Observable<string>;

    public readonly order$: Observable<Order>;
    public order: Order;

    public readonly invPortfolioService: InvPortfolioService = this.injector.get(InvPortfolioService);

    public readonly securityService: SecurityService = this.injector.get(SecurityService);

    /**
     * TODO: Fix isLeverageSelectEnabled$ stream.
     * This stream is used in widget's component to show/hide leverage selector,
     * the same way as in Trade Ticket
     * But in Order Details settings have account, not accountBase,
     * so the stream can't be initialized the right way.
     */
    public readonly isLeverageSelectEnabled$ = new BehaviorSubject<boolean>(false);

    protected readonly orderStreamingService: OrderStreamingService = this.injector.get(OrderStreamingService);

    /**
     * Optional, since it's not used and can't be injected in BO.
     */
    protected readonly orderService: OrderService = this.injector.get(OrderService, null, {optional: true});

    private readonly destroy$ = widgetImplDestroy$(this);
    private readonly translationService: L10nTranslationService = this.injector.get(L10nTranslationService);
    private readonly titleSubject$ = new ReplaySubject<string>(1);

    constructor(
        protected injector: Injector
    ) {
        this.order$ = this.getOrderStream();

        this.title$ = this.titleSubject$.pipe(
            bindWith(this.listenToLiveTitle()),
            takeUntil(this.destroy$),
            share()
        );
    }

    public setSettings(
        settings?: OrderDetailsWidgetSettings,
        flags?: WidgetImplementationEventFlags
    ): Observable<OrderDetailsWidgetSettings> {
        return setWidgetSettings(this, {
            ...settings,
            title: null
        }, flags);
    }

    public updateSettings(
        updates?: Partial<OrderDetailsWidgetSettings>,
        flags?: WidgetImplementationEventFlags
    ): Observable<Partial<OrderDetailsWidgetSettings>> {
        return updateWidgetSettings(this, updates, flags);
    }

    public getSettingsSecurityKeyStream(): Observable<SecurityKey> {
        return this.settingsWithUpdates$.pipe(
            map(settingsUpdates => settingsUpdates.securityKey),
            filter(securityKey => !!securityKey),
            distinctUntilChanged(isSecurityKeysEqual)
        );
    }

    protected getOrderStream(): Observable<Order> {
        const sharedOrder$ = this.settingsWithUpdates$.pipe(
            filter(settingsUpdate =>
                hasSettingUpdateField(settingsUpdate, 'clOrdId')
                || hasSettingUpdateField(settingsUpdate, 'accountId')
            ),
            tap(() => this.order = null),
            switchMap(settingsUpdate =>
                this.getOrderStreamByClOrdIdAndAccountId(
                    getSettingsWithUpdateField(this.settings, settingsUpdate, 'clOrdId'),
                    getSettingsWithUpdateField(this.settings, settingsUpdate, 'accountId')
                )
            ),
            tap(order => {
                this.order = order;

                // Handle the case, when security is available from order only.
                if (!this.securityService.getSecurityFromCacheBySecurityKey(order.security)) {
                    this.securityService.cacheSecurity(order.security);
                }
            }),
            finalize(() => this.order = null),
            share()
        );

        const cachedOrder$ = defer(() =>
            this.order
            && this.order.clOrdId === this.settings.clOrdId
            && this.order.accountId === this.settings.accountId
                ? of(this.order)
                : EMPTY
        );

        return merge(
            cachedOrder$,
            sharedOrder$
        );
    }

    private listenToLiveTitle(): Observable<any> {
        const clOrdId$ = this.settingsWithUpdates$.pipe(
            filter(settings => !!settings.clOrdId)
        );

        return merge(
            this.translationService.onChange(),
            clOrdId$,
            this.order$
        ).pipe(
            tap(() => this.updateTitle()),
            takeUntil(this.destroy$)
        );
    }

    private updateTitle(): void {
        const title: string = [
            this.translationService.translate(this.widgetDescription.widgetLabel),
            this.order && (this.order.orderId || this.order.clOrdId)
        ]
            .filter(value => !!value)
            .join(': ');

        this.titleSubject$.next(title);
    }

    private getOrderStreamByClOrdIdAndAccountId(clOrdId: string, accountId: ExchangeAccountId): Observable<Order> {
        const skipRest$ = new AsyncSubject<void>();
        const skipRest = () => {
            skipRest$.next();
            skipRest$.complete();
        };

        const orderFilter = (order: Order) =>
            order.clOrdId === clOrdId && order.accountId === accountId;

        const fromSettings$ = of(this.settings.order).pipe(
            filter(order => order && orderFilter(order)),
            tap(skipRest)
        );

        const fromStream$ = this.orderStreamingService.order$.pipe(
            squash({
                closingNotifier: WIDGET_UPDATES_GATING$,
                filterBy: orderFilter
            }),
            tap(skipRest)
        );

        /**
         * Wait awhile, allowing to receive order from cache of order streaming service.
         */
        const fromRest$ = timer(100).pipe(
            switchMap(() => {
                const listFilter = ListFilterFactory.createWithSearchFields(
                    [
                        new QueryLangFilterCriterion<Order>({
                            fieldNames: ['clOrdId'],
                            operator:   QueryLangFilterOperator.EQ,
                            query:      clOrdId
                        }),
                        new QueryLangFilterCriterion<Order>({
                            fieldNames: ['accountId'],
                            operator:   QueryLangFilterOperator.EQ,
                            query:      accountId
                        })
                    ],
                    null,
                    null,
                    1
                );

                return this.orderService.getOrders(listFilter).pipe(
                    map(listResponse => listResponse.data[0]),
                    filter(order => !!order)
                );
            }),
            takeUntil(skipRest$)
        );

        return merge(
            fromSettings$,
            fromStream$,
            fromRest$
        );
    }

}
